import { Stack, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import CountryFlag from "@entrilia/shared/components/CountryFlag";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import { countryCodeAlpha2Lookup } from "@entrilia/shared/utilities/countriesProvider";
import type { InvestorBankAccountDetails } from "../../../../api/types/bankAccountTypes";

interface Props {
  bankAccount: InvestorBankAccountDetails;
}

const Row = ({ field, children }: PropsWithChildren<{ field: string }>) => (
  <TableRow>
    <TableCell sx={(t) => ({ width: 200, color: t.palette.text.secondary })}>{field}</TableCell>
    <TableCell sx={{ maxWidth: 250 }}>{children}</TableCell>
  </TableRow>
);

const BankAccountDetailsTable = ({ bankAccount }: Props) => {
  const countryCode = countryCodeAlpha2Lookup.get(bankAccount.country);

  return (
    <RoundedTable>
      <TableBody>
        <Row field="Country">
          <Stack direction="row" spacing={1}>
            {countryCode && <CountryFlag countryCodeAlpha2={countryCode ?? ""} />}
            <Typography>{bankAccount.country}</Typography>
          </Stack>
        </Row>
        <Row field="Bank Name">{bankAccount.bankName}</Row>
        {bankAccount.abaNumber && <Row field="ABA Routing Number">{bankAccount.abaNumber}</Row>}
        {bankAccount.swiftCode && <Row field="SWIFT / BIC Code">{bankAccount.swiftCode}</Row>}
        {bankAccount.address && <Row field="Bank Street Address">{bankAccount.address}</Row>}
        {bankAccount.address2 && <Row field="Bank Street Address 2">{bankAccount.address2}</Row>}
        {bankAccount.city && <Row field="Bank City">{bankAccount.city}</Row>}
        {bankAccount.state && <Row field="Bank State / Province / Region">{bankAccount.state}</Row>}
        {bankAccount.zipCode && <Row field="Bank ZIP / Postal Code">{bankAccount.zipCode}</Row>}
        <Row field="Beneficiary Account Name">{bankAccount.accountName}</Row>
        {bankAccount.iban && <Row field="IBAN">{bankAccount.iban}</Row>}
        {bankAccount.accountNumber && <Row field="Account Number">{bankAccount.accountNumber}</Row>}
        <Row field="Currency">{bankAccount.currencyCode}</Row>
        {bankAccount.institutionNumber && <Row field="Institution Number">{bankAccount.institutionNumber}</Row>}
        {bankAccount.transitNumber && <Row field="Transit Number">{bankAccount.transitNumber}</Row>}
        {bankAccount.ffcName && <Row field="FFC Name">{bankAccount.ffcName}</Row>}
        {bankAccount.ffcAccountNumber && <Row field="FFC Number">{bankAccount.ffcAccountNumber}</Row>}
      </TableBody>
    </RoundedTable>
  );
};

export default BankAccountDetailsTable;
